import React from 'react';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DataService } from 'src/services/data-service';
import GenericDialog from 'src/components/GenericDialog';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IFile } from '.';
import { FileType } from 'src/models/file';

interface IAddFileProps {
  onSuccess: () => void;
  onClose: () => void;
  file?: IFile | null;
}

const AddFile: React.FC<IAddFileProps> = ({ onSuccess, onClose, file }) => {
  const dispatch = useAppDispatch();
  const isEditMode = !!file;

  const formConfig = React.useMemo((): Record<string, DynamicFieldTypes> => ({
    title_En: {
      type: DynamicField.TEXT,
      required: true,
      name: 'title_En',
      title: 'Title (English)',
      value: file?.title.en || '',
      disabled: false
    },
    title_Ar: {
      type: DynamicField.TEXT,
      required: true,
      name: 'title_Ar',
      title: 'Title (Arabic)',
      value: file?.title.ar || '',
      disabled: false
    },
    title_Fr: {
      type: DynamicField.TEXT,
      required: true,
      name: 'title_Fr',
      title: 'Title (French)',
      value: file?.title.fr || '',
      disabled: false
    },
    order: {
      type: DynamicField.NUMBER,
      required: true,
      name: 'order',
      title: 'Order',
      value: file?.order || 0,
      disabled: false
    },
    file: {
      type: DynamicField.FILE,
      required: !isEditMode,
      name: 'file',
      title: 'File',
      value: file?.file || '',
      disabled: false,
      allowedTypes: [
        FileType.pdf,
        FileType.doc,
        FileType.docx,
        FileType.xls,
        FileType.xlsx,
        FileType.jpg,
        FileType.jpeg,
        FileType.png
      ]
    }
  }), [file, isEditMode]);

  const submit = React.useCallback(async (data: Record<string, any>) => {
    try {
      const endpoint = isEditMode ? 'api/files/update' : 'api/files/create';

      const formData = new FormData();

      formData.append('Title_En', data.title_En || '');
      formData.append('Title_Ar', data.title_Ar || '');
      formData.append('Title_Fr', data.title_Fr || '');
      formData.append('Order', String(data.order || 0));

      if (isEditMode) {
        formData.append('Id', file!.id);
        // Keep existing file when editing if no new file was chosen
        if (file!.file && !(data.file instanceof File)) {
          formData.append('FileUrl', file!.file);
        }
      }

      if (data.file instanceof File) {
        formData.append('File', data.file);
      }

      const response = await DataService.postForm(endpoint, formData);

      if (!response.ok) {
        const result: { message?: string } = await response.json();
        dispatch(showAlert({
          message: result?.message || 'An error occurred!',
          type: 'error'
        }));
        return false;
      }

      onSuccess();
      return true;
    } catch (error) {
      dispatch(showAlert({
        message: 'An unexpected error occurred!',
        type: 'error'
      }));
      return false;
    }
  }, [dispatch, onSuccess, isEditMode, file]);

  return (
    <GenericDialog
      open={true}
      title={isEditMode ? 'Edit File' : 'Add File'}
      onClose={onClose}
    >
      <DynamicFormWidget
        title=""
        fields={formConfig}
        onSubmit={submit}
      />
    </GenericDialog>
  );
};

export default AddFile;
